import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { CheckCircle, HourglassLow, Warning } from '@liveauctioneers/hammer-ui-core/icons';
import { memo, useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import RegistrationStatusMessage from '@/components/RegistrationStatus/RegistrationStatusMessage';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import {
    getCatalogCurrency,
    getCatalogIsCatalogOnly,
    getCatalogIsInstantAutopay,
    getCatalogIsTimedPlusBiddingOpen,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { fetchCatalogRegistrationsForUserByIdIfNeeded } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import {
    getIsRegistered,
    getRegistrationApproved,
    getRegistrationApprovedWithLimit,
    getRegistrationBidLimit,
    getRegistrationDeclined,
    getRegistrationPending,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { CompleteAccountModalContext, ModalContext, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { useTrackRegisterForAuctionPressedAnalytics } from '@/utils/amplitude/analytics/registration/trackRegisterForAuctionPressed.analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

type Props = {
    catalogId: number;
    itemId?: number;
    justify?: boolean;
    showRegister?: boolean;
};

const RegistrationStatusLabelComponent = ({ catalogId, itemId, justify, showRegister = true }: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const limited = useAppSelector((state) => getRegistrationApprovedWithLimit(state, catalogId));
    const pending = useAppSelector((state) => getRegistrationPending(state, catalogId));
    const bidLimit = useAppSelector((state) => getRegistrationBidLimit(state, catalogId));
    const currency = useAppSelector((state) => getCatalogCurrency(state, catalogId));
    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const declined = useAppSelector((state) => getRegistrationDeclined(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const timedPlusBiddingOpen = useAppSelector((state) => getCatalogIsTimedPlusBiddingOpen(state, catalogId));
    const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();

    const modalContext = useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: catalogId,
            type: CompleteAccountModalContext.RegisterForAuction,
        }),
        [catalogId]
    );

    const registerForCatalog = () => {
        trackRegisterForAuctionPressedAnalytics();
        dispatch(
            openRegisterForAuctionModal({
                catalogId,
                fromBiddingConsole: false,
                isInstantAutopay,
                itemId,
                modalContext,
            })
        );
    };

    useEffect(() => {
        dispatch(
            fetchCatalogRegistrationsForUserByIdIfNeeded({
                catalogIds: [catalogId],
            })
        );
    }, [catalogId, dispatch]);

    let label = null;

    const needsToRegister = !registered;

    if (needsToRegister) {
        label = showRegister ? (
            <Button
                className="w-[242px]"
                data-testid="catalogRegisterFromBannerButton"
                onClick={registerForCatalog}
                style={timedPlusBiddingOpen ? 'critical' : 'primary'}
                variant="filled"
            >
                {formatMessage([
                    'registration_status_label',
                    isCatalogOnly
                        ? 'request_online_bidding'
                        : timedPlusBiddingOpen
                          ? 'register_to_bid'
                          : 'register_for_auction',
                ])}
            </Button>
        ) : null;
    } else if (isCatalogOnly && registered) {
        label = (
            <span className="text-sm leading-6">
                <RegistrationStatusMessage
                    alertType="approved"
                    header={formatMessage(['registration_status_label', 'request_submitted'])}
                    icon={CheckCircle}
                    justify={justify}
                    noMargin
                />
            </span>
        );
    } else if (pending) {
        label = (
            <span className="text-sm leading-6">
                <RegistrationStatusMessage
                    alertType="pending"
                    header={formatMessage(['registration_status_label', 'registration_pending'])}
                    icon={HourglassLow}
                    justify={justify}
                    noMargin
                />
            </span>
        );
    } else if (limited) {
        label = (
            <span className="text-sm leading-6">
                <RegistrationStatusMessage
                    alertType="approved"
                    header={formatMessage(['bid_limit_approval'], {
                        values: {
                            limit: (
                                <FormattedCurrency
                                    key="bid-limit-approval-limit"
                                    noFraction
                                    sourceCurrency={currency}
                                    value={bidLimit}
                                />
                            ),
                        },
                    })}
                    icon={CheckCircle}
                    justify={justify}
                    noMargin
                />
            </span>
        );
    } else if (approved) {
        label = (
            <span className="text-sm leading-6">
                <RegistrationStatusMessage
                    alertType="approved"
                    header={formatMessage(['registration_status_label', 'registration_approved'])}
                    icon={CheckCircle}
                    justify={justify}
                    noMargin
                />
            </span>
        );
    } else if (declined) {
        label = (
            <span className="text-sm leading-6">
                <RegistrationStatusMessage
                    alertType="declined"
                    header={formatMessage(['registration_status_label', 'registration_declined'])}
                    icon={Warning}
                    justify={justify}
                    noMargin
                />
            </span>
        );
    }

    return label;
};

export const RegistrationStatusLabel = memo<Props>(RegistrationStatusLabelComponent, isEqual);
